import { CalendarPlus, MapPin, Video } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useSettings } from "../lib/settings";

export const CATEGORIES = {
  "charity-times": { label: "Charity Times", chip: "bg-terracotta text-white" },
  priere: { label: "Prière", chip: "bg-olive-soft text-olive-deep border border-olive/25" },
  evangelisation: { label: "Évangélisation", chip: "bg-amber-100 text-amber-900 border border-amber-300/50" },
  fraternel: { label: "Moment fraternel", chip: "bg-sand text-brown border border-line" },
  special: { label: "Événement spécial", chip: "bg-obsidian text-white" },
};

function downloadIcs(ev) {
  const dt = new Date(ev.date);
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;
  const clean = (s) => (s || "").replace(/[\r\n]+/g, " ").replace(/[,;]/g, " ");
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//LaMaisonDeLaCharite//FR",
    "BEGIN:VEVENT",
    `UID:${ev.id}@lamaisondecharite`,
    `DTSTART:${stamp}`,
    "DURATION:PT2H",
    `SUMMARY:${clean(ev.title)}`,
    `LOCATION:${clean(ev.location)}`,
    `DESCRIPTION:${clean(ev.description)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${ev.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function EventCard({ ev }) {
  const { settings } = useSettings();
  const dt = new Date(ev.date);
  const cat = CATEGORIES[ev.category] || CATEGORIES.special;
  const mapsUrl = ev.location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location + " Montévrain")}`
    : settings.maps_url;

  return (
    <article data-testid={`event-card-${ev.id}`} className="card-warm flex gap-5 p-5 sm:p-6">
      {ev.image && (
        <img src={ev.image} alt={ev.title} loading="lazy" className="hidden h-28 w-24 shrink-0 rounded-xl object-cover object-top sm:block" />
      )}
      <div className="flex w-16 shrink-0 flex-col items-center justify-center rounded-xl bg-sand py-3 text-center">
        <span className="font-serif text-2xl font-semibold leading-none text-terracotta">{format(dt, "d")}</span>
        <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">{format(dt, "MMM", { locale: fr })}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${cat.chip}`}>{cat.label}</span>
          <span className="text-xs font-medium text-stone-500">
            {format(dt, "EEEE", { locale: fr })} · {format(dt, "HH'h'mm")}
          </span>
        </div>
        <h3 className="mt-2 font-serif text-lg font-semibold tracking-tight text-obsidian">{ev.title}</h3>
        {ev.location && (
          <p className="mt-1 flex items-center gap-1.5 text-sm text-stone-500">
            <MapPin className="h-3.5 w-3.5 text-terracotta" />
            {ev.location}
          </p>
        )}
        {ev.description && <p className="mt-2 text-sm leading-relaxed text-stone-600">{ev.description}</p>}
        <div className="mt-4 flex flex-wrap gap-2">
          {ev.link && (
            <a
              data-testid={`event-join-btn-${ev.id}`}
              href={ev.link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-terracotta-hover"
            >
              <Video className="h-3.5 w-3.5" />
              Rejoindre sur Zoom
            </a>
          )}
          <button
            data-testid={`event-calendar-btn-${ev.id}`}
            onClick={() => downloadIcs(ev)}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-obsidian transition-colors hover:border-terracotta hover:text-terracotta"
          >
            <CalendarPlus className="h-3.5 w-3.5" />
            Ajouter à mon calendrier
          </button>
          <a
            data-testid={`event-itinerary-btn-${ev.id}`}
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-obsidian transition-colors hover:border-terracotta hover:text-terracotta"
          >
            <MapPin className="h-3.5 w-3.5" />
            Itinéraire
          </a>
        </div>
      </div>
    </article>
  );
}
